import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accountActions } from '../../redux/actions';
import { Modal, Row, Col, Checkbox, Input, Typography } from 'antd';
import Localization from '../../utils/localization';
import './styles.scss';
import { accountServices } from '../../redux/services';

const { Title } = Typography;

const CreateChangeIn = ({ accountStructure, isCreateChangeInOpen, setIsCreateChangeInOpen, mainAccount }) => {
    const dispatch = useDispatch();
    const creatingChangeInAccount = useSelector((state) => state.accounts.creatingChangeInAccount);

    const [accountNumber, setAccountNumber] = useState('');
    const [accountName, setAccountName] = useState('');
    const [mainAccountGroupId, setMainAccountGroupId] = useState(null);
    const [mainAccountFinancialGroupType, setMainAccountFinancialGroupType] = useState(null);
    const [cashEffect, setCashEffect] = useState(true);

    const findNewAssociatedAccountNumber = useCallback(
        (mainAccount) => {
            let newAccountNumber = '';
            if (mainAccount.children != null) {
                const re = /_\d_C|_\d_NC$/;

                const prevAccNum = mainAccount.children
                    .flatMap((account) => re.exec(account.account_number))
                    .filter((number) => number !== null)
                    .map((number) => number.replace(/[^0-9]/g, ''))
                    .sort()
                    .pop();

                const nameCashEffect = cashEffect ? `${+prevAccNum + 1}C` : `${+prevAccNum + 1}NC`;
                newAccountNumber = mainAccount.account_number + nameCashEffect;
            } else {
                const nameNoneCashEffect = cashEffect ? '0C' : '0NC';
                newAccountNumber = mainAccount.account_number + nameNoneCashEffect;
            }
            return newAccountNumber;
        },
        [cashEffect],
    );

    useEffect(() => {
        if (!mainAccount) return;
        setAccountNumber(findNewAssociatedAccountNumber(mainAccount, accountStructure));
        setAccountName(mainAccount.name + 'CI');
        setMainAccountGroupId(mainAccount.group_id);
        setMainAccountFinancialGroupType(mainAccount.financial_group_type);
    }, [findNewAssociatedAccountNumber, accountStructure, mainAccount]);

    const findAccountChildren = (mainAccountId, accountStructure) => {
        const mainAccount = {};
        accountServices.findAccount(mainAccount, mainAccountId, accountStructure);

        const children = mainAccount.copy.children != null ? mainAccount.copy.children : [];
        return children;
    };

    const handleOk = () => {
        const changeInAccount = {};
        const children = findAccountChildren(mainAccount.account_id, accountStructure);

        changeInAccount.account_id = mainAccount.account_id;
        changeInAccount.organization_id = mainAccount.organization_id;
        changeInAccount.account_number = accountNumber;
        changeInAccount.name = accountName;
        changeInAccount.parent = mainAccount.account_id;
        changeInAccount.group_id = mainAccountGroupId;
        changeInAccount.financial_group_type = mainAccountFinancialGroupType;
        changeInAccount.roll_up = mainAccount.roll_up;
        changeInAccount.carry = false;
        changeInAccount.convergence = false;
        changeInAccount.exclude = false;
        changeInAccount.key = accountNumber;
        changeInAccount.index = children.length > 0 ? children.length : 0;

        if (!creatingChangeInAccount) dispatch(accountActions.createChangeInAccount(cashEffect, changeInAccount));
        handleCancel();
    };

    const handleCancel = () => {
        setIsCreateChangeInOpen(false);
    };

    const onChangeAccountNumber = (e) => {
        setAccountNumber(e.target.value);
    };

    const onChangeAccountName = (e) => {
        setAccountName(e.target.value);
    };

    const onCheckCashEffect = () => {
        setCashEffect(true);
    };

    const onCheckNonCashEffect = () => {
        setCashEffect(false);
    };

    const renderCreateChangeIn = () => {
        return (
            <div>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '30px' }}>
                            {' '}
                            {Localization.accountStructure.accountMainAccountNumber}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            value={mainAccount.account_number}
                            placeholder={Localization.accountStructure.accountNumberNotFound}
                            style={{ textAlign: 'right', marginTop: '30px' }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountMainAccountName}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            value={mainAccount.name}
                            placeholder={Localization.accountStructure.accountNameNotFound}
                            style={{ textAlign: 'right', marginTop: '15px' }}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountChangeInAccountNumber}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            type="text"
                            value={accountNumber}
                            placeholder={Localization.accountStructure.accountNumberNotFound}
                            style={{ textAlign: 'right', marginTop: '15px' }}
                            onChange={onChangeAccountNumber}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountChangeInAccountName}{' '}
                        </Title>
                    </Col>
                    <Col span={12}>
                        <Input
                            type="text"
                            value={accountName}
                            placeholder={Localization.accountStructure.accountNameNotFound}
                            style={{ textAlign: 'right', marginTop: '15px' }}
                            onChange={onChangeAccountName}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountEffect}{' '}
                        </Title>
                    </Col>
                    <Col span={8}>
                        <Title level={5} style={{ marginTop: '15px' }}>
                            {' '}
                            {Localization.accountStructure.accountEffectCash}{' '}
                        </Title>
                    </Col>
                    <Col span={8}>
                        <Checkbox
                            checked={cashEffect}
                            style={{ marginTop: '15px', float: 'right' }}
                            onChange={onCheckCashEffect}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col span={8}></Col>
                    <Col span={8}>
                        <Title level={5} style={{ marginTop: '0' }}>
                            {' '}
                            {Localization.accountStructure.accountEffectNonCash}{' '}
                        </Title>
                    </Col>
                    <Col span={8}>
                        <Checkbox
                            checked={!cashEffect}
                            style={{ marginTop: '0', float: 'right' }}
                            onChange={onCheckNonCashEffect}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <Modal
            bodyStyle={{
                minHeight: '30vh',
                display: 'flex',
                flexDirection: 'column',
            }}
            title={Localization.accountStructure.accountCreateChangeIn}
            visible={isCreateChangeInOpen} // not deprecated in our version
            onOk={handleOk}
            confirmLoading={false}
            onCancel={handleCancel}
        >
            {renderCreateChangeIn()}
        </Modal>
    );
};

export default CreateChangeIn;
